import {useEffect, useState} from 'react';
import {Dimensions, Platform} from 'react-native';
import {useMediaQuery} from 'react-responsive';

import '../utils/match-media';

import ScreenDimensions from '../constants/ScreenDimensionsConfig';

export const ScreenTypes = {
  mobile: 'mobile',
  desktop: 'desktop',
  desktopLarge: 'desktopLarge',
};

type ScreenConfig = {
  screenMaxWidth: number;
  screenWidth: number;
  appScreenWidthPercentage: number;
  appWidth: number;
  isWebDesktopScreen: boolean;
  screenType: string;
  platform: 'Web Mobile' | 'Web Desktop' | 'IOS App' | 'Android App' | 'Unknown';
};

/**
 * TODO
 * - refactor this hook to use React-Native features
 * - review and remove useMediaQuery, unsure why this was specifically used in the first place
 * - possibly debounce `deviceScreenWidth` if we run into performance issues
 */
export function useWebScreenDimensions(): ScreenConfig {
  const [deviceScreenWidth, setDeviceScreenWidth] = useState(Dimensions.get('window').width);

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({window}) => {
      setDeviceScreenWidth(window.width);
    });

    return () => subscription?.remove();
  }, []);

  const screenConfig: ScreenConfig = {
    screenMaxWidth: Infinity,
    screenWidth: 0,
    appScreenWidthPercentage: 100,
    appWidth: 0,
    isWebDesktopScreen: false,
    screenType: '',
    platform: 'Unknown',
  };

  const isMobileOrTablet = Platform.select({
    web: useMediaQuery({
      maxWidth: ScreenDimensions.mobileOrTablet.maxWidth,
    }),
    native: deviceScreenWidth < ScreenDimensions.mobileOrTablet.maxWidth,
    default: false,
  });

  const isDesktopSmallMediumScreen = Platform.select({
    web: useMediaQuery({
      maxWidth: ScreenDimensions.desktopSmallMedium.maxWidth,
    }),
    default:
      ScreenDimensions.mobileOrTablet.maxWidth <= deviceScreenWidth &&
      deviceScreenWidth < ScreenDimensions.desktopSmallMedium.maxWidth,
  });

  let screenDimensionsConfig = null;
  if (isMobileOrTablet) {
    screenConfig.screenType = ScreenTypes.mobile;
    screenDimensionsConfig = ScreenDimensions.mobileOrTablet;
  } else if (isDesktopSmallMediumScreen) {
    screenConfig.screenType = ScreenTypes.desktop;
    screenDimensionsConfig = ScreenDimensions.desktopSmallMedium;
  } else {
    screenConfig.screenType = ScreenTypes.desktopLarge;
    screenDimensionsConfig = ScreenDimensions.desktopLarge;
  }

  screenConfig.appWidth =
    (deviceScreenWidth * screenDimensionsConfig.appScreenWidthPercentage) / 100;
  screenConfig.screenWidth = deviceScreenWidth;
  screenConfig.screenMaxWidth = screenDimensionsConfig.maxWidth;
  screenConfig.appScreenWidthPercentage = screenDimensionsConfig?.appScreenWidthPercentage;

  screenConfig.isWebDesktopScreen =
    Platform.OS === 'web' && screenConfig.screenType !== ScreenTypes.mobile;

  screenConfig.platform = Platform.select({
    web: isMobileOrTablet ? 'Web Mobile' : 'Web Desktop',
    ios: 'IOS App',
    android: 'Android App',
    default: 'Unknown',
  });

  return screenConfig;
}

export type WebScreenDimensionsType = ReturnType<typeof useWebScreenDimensions>;
